import {
  Box,
  Button,
  CheckIcon,
  Divider,
  Grid2,
  NumberIcon,
  PhoneIcon,
  StatusField,
  Typography,
} from "components";
import { AdvisorOnboardFormInterface } from "interfaces/OnboardingInterface";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useAppSelector } from "store/store";
import { numberToCurrency } from "utils";
import { handleReturnAmount } from "utils/helpers";
import AdvisorIncompleteMFKYCModel from "./AdvisorIncompleteMFKYCModel";

const AdvisorOnboardingCompleted = () => {
  const form = useFormContext<AdvisorOnboardFormInterface>();
  const [showMFKycModal, setShowMFKycModal] = useState(false);

  const onBoardflowData = useAppSelector(
    (state) => state?.app?.onBoardflowData,
  );
  const onboardingType = form.getValues("onboardingType") || [];
  const isOnlyMfOnboarding =
    onboardingType.includes("MF") && !onboardingType.includes("RIA");

  const fpOnboardingSession = (form.getValues() as any)?.fpOnboardingSession;
  const isMFKYCComplete =
    fpOnboardingSession?.fpPreVerificationStatus === "completed" &&
    fpOnboardingSession?.fpReadinessStatus === "verified";

  const onBoardingDetails = [
    {
      name: "Basket Name",
      value: form.getValues("basketObject.name") || "-",
      show:
        !isOnlyMfOnboarding && onBoardflowData.customizeSetting.basketFeePlan,
      statusComponent: false,
    },
    {
      name: "KYC",
      value: form.getValues("kyc.kycStatus") || "-",
      show: !isOnlyMfOnboarding && onBoardflowData.customizeSetting.kyc,
      statusComponent: true,
    },
    {
      name: "Risk Profiling",
      value: form.getValues("riskProfile.riskStatus") || "-",
      show:
        !isOnlyMfOnboarding && onBoardflowData.customizeSetting.riskprofiling,
      statusComponent: true,
    },
    {
      name: "Fee Plan",
      value: form.getValues("selectedFeePlan.name") || "-",
      show:
        !isOnlyMfOnboarding && onBoardflowData.customizeSetting.basketFeePlan,
      statusComponent: false,
    },
    {
      name: "Agreement",
      value: form.getValues("agreement.agreementStatus.status") || "Pending",
      show: !isOnlyMfOnboarding && onBoardflowData.customizeSetting.agreement,
      statusComponent: true,
    },
    {
      name: "Payment Amount",
      value:
        numberToCurrency(
          handleReturnAmount({
            skipAdvanceFee: form.getValues("skipAdvanceFee"),
            feePlanDetails: form.getValues("advanceFeePlan.feePlan"),
            advanceFeePlan: form.getValues("advanceFeePlan"),
            planDataObject: form.getValues("planDataObject"),
            isExitGST: onBoardflowData?.revenueWithGst,
          }) || 0,
        ) || "-",
      show: !isOnlyMfOnboarding && onBoardflowData.customizeSetting.payment,
      statusComponent: false,
    },
    {
      name: "MF Investor Readiness",
      value: isMFKYCComplete
        ? "Completed"
        : form.getValues("agreement.agreementStatus.mfReadness") ||
          "Incomplete",
      show: onBoardflowData.customizeSetting.mfReadness || isOnlyMfOnboarding,
      statusComponent: true,
      extraButton: !isMFKYCComplete ? (
        <>
          <Button
            onClick={() => {
              setShowMFKycModal(true);
            }}
          >
            Complete KYC
          </Button>
        </>
      ) : undefined,
    },
  ];

  return (
    <>
      <Grid2 spacing={2} container>
        <Grid2 container size={12}>
          <Grid2
            size={{ xs: 12 }}
            spacing={2}
            container
            justifyContent={"center"}
            flexDirection={"column"}
            p={2}
            bgcolor={"var(--grey-bg-100)"}
            borderRadius={2}
          >
            <Typography fontWeight={600} fontSize={18}>
              {form.getValues("personalDetails.name") || "-"}
            </Typography>
            <Box display={"flex"} alignItems={"center"} gap={2}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <PhoneIcon />
                <Typography fontWeight={500} color={"#697586"} fontSize={12}>
                  Phone No.
                </Typography>
                <Typography fontWeight={500} color={"#697586"} fontSize={14}>
                  {form.getValues("phone") || "-"}
                </Typography>
              </Box>
              <Divider orientation="vertical" />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <NumberIcon />
                <Typography fontWeight={500} color={"#697586"} fontSize={12}>
                  Client Code
                </Typography>
                <Typography fontWeight={500} color={"#697586"} fontSize={14}>
                  {form.getValues("clientCode") || "-"}
                </Typography>
              </Box>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12 }} container>
            {onBoardingDetails
              .filter((dataItem) => dataItem.show)
              .map((dataItem) => {
                return (
                  <Grid2
                    container
                    size={12}
                    p={1}
                    borderBottom={"dashed 1px var(--neutral-100)"}
                  >
                    <Grid2
                      size={3}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <CheckIcon
                        color={"var(--success-700)"}
                        height={16}
                        width={16}
                      />
                      <Typography
                        fontWeight={500}
                        color={"#697586"}
                        fontSize={12}
                      >
                        {dataItem.name}:
                      </Typography>
                    </Grid2>
                    <Grid2
                      size={7}
                      display={"flex"}
                      alignItems={"center"}
                      gap={2}
                    >
                      {dataItem.statusComponent ? (
                        <StatusField statusString={dataItem.value} />
                      ) : (
                        <Typography
                          fontWeight={500}
                          color={"#333"}
                          fontSize={14}
                        >
                          {dataItem.value}
                        </Typography>
                      )}
                      {dataItem.extraButton && dataItem.extraButton}
                    </Grid2>
                  </Grid2>
                );
              })}
          </Grid2>
        </Grid2>
      </Grid2>
      <AdvisorIncompleteMFKYCModel
        open={showMFKycModal}
        setOpen={setShowMFKycModal}
        id={form.getValues("id")}
      />
    </>
  );
};

export default AdvisorOnboardingCompleted;
